import React, { useEffect, useState } from 'react';
import { useParams, Outlet, NavLink, Link, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import Select from 'react-select';

import CommonAnimatedLoader from '../../components/common/animated/loader';
import { useTest } from '../../context/test';
import * as usersService from '../../services/api/users';
import * as testsService from '../../services/api/tests';
import { useAuth } from '../../hooks/use-auth';
import TestStatusBadge from '../../components/tests/status-badge';
import { TestStatus } from '../../entities/test';
import CommonAnimatedSpinner from '../../components/common/animated/spinner';
import CommonConfirmationModal from '../../components/modals/confirmation';
import { Allergist } from '../../entities/form/allergist';
import CommonButtonDefault from '../../components/common/buttons/default';
import StyledError from '../../components/common/form/styled-error';
import Badge from '../../components/common/badge';
import { getAgeFromBirthDate } from '../../services/formatter/date';

function TestsResultsPageComponent() {
  const { id } = useParams();
  const { token, user } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isConfirming, setIsConfirming] = useState<boolean>(false);
  const [isCancelling, setIsCancelling] = useState<boolean>(false);
  const [doctors, setDoctors] = useState<Allergist[]>();
  const [error, setError] = useState<string>();
  const location = useLocation();
  const { setTestId, updateTest, testView } = useTest();

  const baseTabs = [
    { name: 'Questionnaire', href: `/tests/results/${id}`, current: true },
    { name: 'Blood results', href: `/tests/results/${id}/blood`, current: false },
    { name: 'Allergens', href: `/tests/results/${id}/answer`, current: false },
    { name: 'Comments', href: `/tests/results/${id}/comments`, current: false },
    { name: 'Recommendations', href: `/tests/results/${id}/recommendations`, current: false },
  ];

  const hasPatientHistory =
    testView &&
    ((testView.otherTestsCount && testView.otherTestsCount > 0) ||
      (testView.otherOrdersCount && testView.otherOrdersCount > 0));

  const tabs = hasPatientHistory
    ? [...baseTabs, { name: 'Patient History', href: `/tests/results/${id}/other-tests`, current: false }]
    : baseTabs;

  for (const tab of tabs) {
    tab.current = tab.href === location.pathname;
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const allergists = await usersService.getAdministratorsList(token, { showInAdmin: 1 });

        if (allergists) {
          setDoctors(allergists);
        }
      } catch (e) {
        setError((e as Error).message);
      }
    };

    if (id) {
      setTestId(parseInt(id, 10));
      fetchData();
      setIsLoading(false);
    }
  }, [token, id, setTestId]);

  const onCompleteTest = async () => {
    setIsConfirming(false);
    setIsSaving(true);
    if (testView) {
      await testsService.complete(token, testView.test.id);

      updateTest({
        ...testView.test,
        status: TestStatus.Completed,
        completedAt: new Date().toISOString(),
      });
    }
    setIsSaving(false);
  };

  const onCancelTest = async () => {
    setIsCancelling(false);
    setIsSaving(true);
    if (testView) {
      await testsService.cancel(token, testView.test.id);

      updateTest({
        ...testView.test,
        status: TestStatus.Cancelled,
      });
    }
    setIsSaving(false);
  };

  const onSelectDoctor = async (allergist: Allergist) => {
    if (id) {
      if (allergist.id === null) {
        // Unassign doctor
        await testsService.update(token, parseInt(id, 10), { administratorId: null as unknown as number });
        if (testView) {
          updateTest({
            ...testView.test,
            administratorId: undefined,
            administratorFirstName: undefined,
            administratorLastName: undefined,
          });
        }
      } else if (allergist.id) {
        await testsService.update(token, parseInt(id, 10), { administratorId: allergist.id });
        if (testView) {
          updateTest({
            ...testView.test,
            administratorId: allergist.id,
            administratorFirstName: allergist.firstName,
            administratorLastName: allergist.lastName,
          });
        }
      } else {
        setError('Doctor ID is missing');
      }
    }
  };

  const onAssignToMe = async () => {
    if (user && user.id) {
      await onSelectDoctor({
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
      } as Allergist);
    }
  };

  const onSelectPreview = () => {
    if (testView && testView.order) {
      const url = testView?.frontendUrl || 'https://app.allergomedica.lt';

      if (url) {
        window.open(`${url}/results/redirect/${testView.order.hash}/${token}`, '_blank');
      }
    } else {
      setError('Test or order is missing');
    }
  };

  const doctorOptions = [
    ...(testView?.test.administratorId ? [{ value: null, label: '-- Unassign --' }] : []),
    ...(doctors?.map((doctor) => ({
      value: doctor.id,
      label: `${doctor.firstName} ${doctor.lastName}`,
    })) || []),
  ];

  let selectedDoctor = testView?.test.administratorId
    ? doctorOptions?.find((d) => d.value === testView.test.administratorId)
    : undefined;

  // If doctor is assigned but not in the list (e.g., doesn't have showInAdmin), add them manually
  if (
    testView?.test.administratorId &&
    !selectedDoctor &&
    testView.test.administratorFirstName &&
    testView.test.administratorLastName
  ) {
    selectedDoctor = {
      value: testView.test.administratorId,
      label: `${testView.test.administratorFirstName} ${testView.test.administratorLastName}`,
    };
  }

  return (
    <div>
      {(isLoading || testView === undefined) && <CommonAnimatedLoader />}
      {error && <StyledError title={error} />}
      {testView && (
        <div>
          <div className="pb-5 pt-5 bg-white border-b border-gray-200 sm:pb-0">
            <div className="px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate mb-1">
                    Test #{testView.test.id}
                  </h2>
                  <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-y-2 gap-x-6 text-sm text-gray-500">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">Patient:</span>
                      {testView.test.patientName && testView.test.patientLastName ? (
                        <>
                          {testView.test.patientHash ? (
                            <Link
                              to={`/patients/preview/${testView.test.patientHash}`}
                              className="text-indigo-600 hover:text-indigo-900 font-medium"
                            >
                              {testView.test.patientName} {testView.test.patientLastName}
                            </Link>
                          ) : (
                            <span className="text-gray-900">
                              {testView.test.patientName} {testView.test.patientLastName}
                            </span>
                          )}
                          {(testView.test.patientAge !== undefined || testView.test.patientBirthDate) && (
                            <span className="text-gray-500 ml-1">
                              Age:{' '}
                              {testView.test.patientBirthDate
                                ? getAgeFromBirthDate(testView.test.patientBirthDate)
                                : testView.test.patientAge}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-gray-900">{testView.order?.email || 'N/A'}</span>
                      )}
                      {testView.test.patientIsChild && <Badge text="Child" status="primary" />}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <div className="w-64">
                    <Select
                      options={doctorOptions}
                      value={selectedDoctor}
                      onChange={(option) =>
                        option &&
                        onSelectDoctor({
                          id: option.value,
                          firstName: '',
                          lastName: '',
                        } as Allergist)
                      }
                      placeholder="Select doctor..."
                      className="text-sm"
                    />
                  </div>
                  {user && user.id && !testView?.test.administratorId && (
                    <CommonButtonDefault onClick={onAssignToMe} className="text-sm whitespace-nowrap">
                      Assign to me
                    </CommonButtonDefault>
                  )}
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-between gap-4 border-t border-gray-100 pt-4">
                <div className="flex flex-wrap items-center gap-4">
                  {' '}
                  {/* Combined info bar */}
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">Order:</span>
                    <Link
                      to={`/orders/results/${testView.test.orderId}`}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      #{testView.test.orderId}
                    </Link>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">Product:</span>
                    <div className="flex items-center gap-2">
                      {testView.test.productKey && (
                        <span className="font-mono text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-800">
                          {testView.test.productKey}
                        </span>
                      )}
                      <span>{testView.test.productTitle}</span>
                      {testView.test.isAtHome && <Badge text="At Home" status="success" />}
                      {testView.test.hasConsultation && <Badge text="+ Consultation" status="primary" />}
                      {testView.test.hasExtendedInterpretation && <Badge text="+ Interpretation" status="primary" />}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Status:</span>
                    <TestStatusBadge status={testView.test.status} />
                  </div>
                  {testView.test.isBloodRegistered && <Badge text="Blood Registered" status="success" />}
                  {testView.order?.thrivaOrderId && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      <span>Thriva</span>
                      <span className="font-mono text-purple-600">{testView.order.thrivaOrderId}</span>
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <CommonButtonDefault onClick={onSelectPreview} className="text-sm">
                    Preview results
                  </CommonButtonDefault>
                  {testView.test.status !== TestStatus.Completed && testView.test.status !== TestStatus.Cancelled && (
                    <>
                      <CommonButtonDefault onClick={() => setIsConfirming(true)} disabled={isSaving}>
                        {isSaving ? 'Completing...' : 'Complete Test'}
                      </CommonButtonDefault>
                      <button
                        type="button"
                        onClick={() => setIsCancelling(true)}
                        disabled={isSaving}
                        className="inline-flex items-center px-4 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        Cancel Test
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="mt-4">
              <div className="sm:hidden">
                <label htmlFor="tabs" className="sr-only">
                  Select a tab
                </label>
                <select
                  id="tabs"
                  name="tabs"
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                  defaultValue={tabs.find((tab) => tab.current)?.name}
                >
                  {tabs.map((tab) => (
                    <option key={tab.name}>{tab.name}</option>
                  ))}
                </select>
              </div>
              <div className="hidden sm:block">
                <div className="border-b border-gray-200">
                  <nav className="-mb-px flex space-x-8 px-4 sm:px-6 lg:px-8" aria-label="Tabs">
                    {tabs.map((tab) => (
                      <NavLink
                        key={tab.name}
                        to={tab.href}
                        className={classNames(
                          tab.current
                            ? 'border-indigo-500 text-indigo-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                          'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm',
                        )}
                        aria-current={tab.current ? 'page' : undefined}
                      >
                        {tab.name}
                      </NavLink>
                    ))}
                  </nav>
                </div>
              </div>
            </div>
          </div>

          {/* Tab Content */}
          <div className="px-4 sm:px-6 lg:px-8 py-6">
            <Outlet />
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {isConfirming && (
        <CommonConfirmationModal
          open={isConfirming}
          title="Complete Test"
          description="Are you sure you want to complete this test? This will send a result email to the customer."
          confirmed={onCompleteTest}
          cancelled={() => setIsConfirming(false)}
        />
      )}

      {/* Cancel Confirmation Modal */}
      {isCancelling && (
        <CommonConfirmationModal
          open={isCancelling}
          title="Cancel Test"
          description="Are you sure you want to cancel this test? This will also cancel the associated order."
          confirmed={onCancelTest}
          cancelled={() => setIsCancelling(false)}
        />
      )}

      {isSaving && <CommonAnimatedSpinner />}
    </div>
  );
}

export default TestsResultsPageComponent;
